import React, { useEffect, useState, type FC } from 'react';
import { FilterTag, Wrapper } from './filterTag';
import type { Carline } from '../../utils/filter';
import { useUniversalState } from '../../context/UniversalStateContext';
import type { CarlineGroup } from '../../../@types/mapped';
import type { TrackingManager } from '../../hooks/useTrackingManager';
import withTracking from '../../hoc/withTracking';
import { StyledIconButton } from './FilterBodyTypeTags';

interface FilterCarlineTagsProps {
  readonly carlineGroups: CarlineGroup[];
  readonly filteredCarlines: Carline[] | undefined;
  readonly selectedCarlineGroups: string[];
  readonly createOnClickHandler: (id: string) => () => void;
  readonly cutItemsAfter?: number;
  readonly size?: 'sm' | 'md';
  readonly trackingManager: TrackingManager;
}

const FilterCarlineTags: FC<FilterCarlineTagsProps> = ({
  carlineGroups,
  filteredCarlines,
  selectedCarlineGroups,
  createOnClickHandler,
  cutItemsAfter,
  trackingManager,
  size = 'md',
}) => {
  if (!filteredCarlines) {
    return undefined;
  }
  const { translations } = useUniversalState();

  const [isShortened, setIsShortened] = useState<boolean>(
    cutItemsAfter ? carlineGroups.length > cutItemsAfter : false,
  );
  const firstRevealedId =
    isShortened && cutItemsAfter ? carlineGroups[cutItemsAfter]?.identifier.id : undefined;

  useEffect(() => {
    setIsShortened(cutItemsAfter ? carlineGroups.length > cutItemsAfter : false);
  }, [carlineGroups.length, cutItemsAfter]);

  const handleShowMoreClick = () => {
    setIsShortened(false);
    trackingManager?.trackShowMoreFilterClick('models');
    // a11y hack as tags component does not accept ref/forwardref
    setTimeout(() => {
      if (firstRevealedId) {
        const selector = `[data-testid="filterModelle-${firstRevealedId.replace(/\s+/g, '')}"] input[type="checkbox"]`;
        const input = document.querySelector(selector);
        if (input instanceof HTMLElement) {
          input.focus();
        }
      }
    }, 50);
  };

  let tags = carlineGroups?.map(({ identifier: { id } }) => {
    const isSelected = selectedCarlineGroups.includes(id);
    const isDisabled =
      filteredCarlines &&
      filteredCarlines.length > 0 &&
      !filteredCarlines?.some((carline) => carline.group === id) &&
      !isSelected;

    return (
      <FilterTag
        key={id}
        id={id}
        text={translations[`carlineGroup.${id}`]}
        onClickHandler={createOnClickHandler(id)}
        selected={isSelected}
        disabled={isDisabled}
        dataTestId={`filterModelle-${id?.replace(/\s+/g, '')}`}
        size={size}
      />
    );
  });
  if (isShortened) {
    tags = tags.slice(0, cutItemsAfter);
  }
  return (
    <div translate="no">
      <Wrapper size={size}>
        {React.Children.map(tags, (tag) => tag)}
        {isShortened && (
          <StyledIconButton
            href="#"
            icon="more"
            onClick={handleShowMoreClick}
            size="sm"
            title="Reveal all filter"
            variant="tertiary"
            data-testid="expandModelleFilter"
          />
        )}
      </Wrapper>
    </div>
  );
};
export const TrackedFilterCarlineTags = withTracking(FilterCarlineTags);
